import { useEffect, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import authService from '../services/authService'
import { HiOutlineUser, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff, HiOutlineOfficeBuilding } from 'react-icons/hi'
import toast from 'react-hot-toast'

const SSO_ERROR_MESSAGES = {
  sso_disabled: 'Chức năng SSO chưa được bật.',
  sso_init_failed: 'Không thể bắt đầu đăng nhập SSO.',
  state_mismatch: 'Phiên đăng nhập đã hết hạn. Vui lòng thử lại.',
  missing_code: 'SSO không trả mã xác thực.',
  token_exchange_failed: 'Không thể xác thực với SSO.',
  userinfo_failed: 'Không thể lấy thông tin tài khoản SSO.',
  invalid_userinfo: 'Thông tin tài khoản SSO không hợp lệ.',
  user_inactive: 'Tài khoản ĐXMT của bạn chưa hoạt động hoặc đã bị khóa. Vui lòng kiểm tra lại trên hệ thống SSO.',
  local_user_disabled: 'Tài khoản của bạn đã bị vô hiệu hóa trên hệ thống tuyển dụng. Vui lòng liên hệ admin.',
  unauthorized_role: 'Tài khoản ĐXMT của bạn chưa được cấp quyền vào hệ thống tuyển dụng. Vui lòng liên hệ HCNS/Admin.',
  sso_exchange_failed: 'Phiên đăng nhập SSO đã hết hạn. Vui lòng thử lại.',
  sso_login_required: 'Bạn cần đăng nhập SSO để tiếp tục.',
  sso_access_denied: 'Bạn đã hủy hoặc không cấp quyền đăng nhập SSO.',
  sso_provider_error: 'SSO provider trả về lỗi. Vui lòng thử lại.',
}

function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isLoading, error, clearError } = useAuthStore()

  const [formData, setFormData] = useState({ username: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [isSsoLoading, setIsSsoLoading] = useState(false)
  const [showLocalLogin, setShowLocalLogin] = useState(false)   // ưu tiên SSO; login nội bộ ẩn sau dòng chữ nhỏ
  const [ssoEnabled, setSsoEnabled] = useState(null)            // null = đang hỏi; false = SSO tắt -> chỉ form nội bộ
  const ssoError = new URLSearchParams(location.search).get('error')
  const ssoErrorMessage = ssoError
    ? (SSO_ERROR_MESSAGES[ssoError] || 'Đăng nhập SSO thất bại. Vui lòng thử lại.')
    : null

  useEffect(() => {
    authService.ssoStatus()
      .then((res) => setSsoEnabled(!!res.data?.enabled))
      .catch(() => setSsoEnabled(false))
  }, [])

  // Giữ cả query string (?open=&cv=) để quay lại đúng lead sau khi đăng nhập
  const fromLoc = location.state?.from
  const from = fromLoc ? (fromLoc.pathname + (fromLoc.search || '')) : '/admin'

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (error) clearError()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isSsoLoading) return
    if (!formData.username || !formData.password) {
      toast.error('Vui lòng nhập đầy đủ thông tin')
      return
    }
    const result = await login(formData)
    if (result.success) {
      toast.success('Đăng nhập thành công!')
      navigate(from, { replace: true })
    }
  }

  const handleSsoLogin = () => {
    setIsSsoLoading(true)
    const params = new URLSearchParams({ redirect: from })
    window.location.href = `/api/auth/sso/login?${params.toString()}`
  }

  const localForm = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label" htmlFor="username">Tên đăng nhập / Email / SĐT</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <HiOutlineUser className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="username"
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            className="input pl-10 focus:ring-2 focus:ring-primary-500"
            placeholder="Nhập tên đăng nhập"
            autoComplete="username"
          />
        </div>
      </div>

      <div>
        <label className="label" htmlFor="password">Mật khẩu</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <HiOutlineLockClosed className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="input pl-10 pr-10 focus:ring-2 focus:ring-primary-500"
            placeholder="Nhập mật khẩu"
            autoComplete="current-password"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
          >
            {showPassword
              ? <HiOutlineEyeOff className="h-5 w-5 text-gray-400" />
              : <HiOutlineEye className="h-5 w-5 text-gray-400" />}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm leading-5 text-red-700">{error}</p>
        </div>
      )}

      <button type="submit" disabled={isLoading || isSsoLoading} className="btn-primary w-full py-3 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70">
        {isLoading ? 'Đang xử lý...' : 'Đăng nhập'}
      </button>
    </form>
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-600 to-primary-900 px-4 py-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-md items-center justify-center">
        <div className="w-full rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-white/20 sm:p-8">
          <div className="mb-7 text-center">
            <h1 className="text-2xl font-bold text-secondary-900">Quản lý tuyển dụng</h1>
          </div>

          {ssoEnabled !== false && ssoErrorMessage && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-sm leading-5 text-red-700">{ssoErrorMessage}</p>
            </div>
          )}

          {ssoEnabled === null ? (
            <div className="flex justify-center py-10"><div className="spinner h-7 w-7" /></div>
          ) : !ssoEnabled ? (
            localForm
          ) : (
          <>
          <button
            type="button"
            onClick={handleSsoLogin}
            disabled={isSsoLoading || isLoading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <HiOutlineOfficeBuilding className="h-5 w-5" />
            {isSsoLoading ? 'Đang chuyển đến SSO...' : 'Đăng nhập qua SSO'}
          </button>

          {!showLocalLogin ? (
            <p className="mt-5 text-center">
              <button
                type="button"
                onClick={() => setShowLocalLogin(true)}
                className="text-sm text-secondary-500 underline underline-offset-2 transition hover:text-primary-600"
              >
                Đăng nhập bằng tài khoản nội bộ
              </button>
            </p>
          ) : (
          <>
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-secondary-200" />
            <span className="text-xs font-medium uppercase tracking-wide text-secondary-400">tài khoản nội bộ</span>
            <div className="h-px flex-1 bg-secondary-200" />
          </div>

          {localForm}
          </>
          )}

          </>
          )}

          <p className="mt-6 text-center text-sm text-secondary-500">
            © 2026 Đất Xanh Miền Trung · <Link to="/huong-dan" className="text-primary-600 hover:underline">Hướng dẫn sử dụng</Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
